/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { DragDropModule } from '@angular/cdk/drag-drop';
import { ModuleWithProviders, NgModule, NO_ERRORS_SCHEMA } from '@angular/core';

import { CommonInternalSharedModule } from '@gh/common-internal-shared';
import { ApplicationStateFactory, AuthGuard, FormSchemaFactory, UserContext } from '@gh/common-shared';
import { TYPE_SET } from '@gh/core-type';
// @ts-ignore
import { MonkeyDropListNoMoveDirective } from '@gh/core-ui/monkey/monkey-drop-list-no-move.directive';
import { MonkeyDropListSortingDisabledDirective } from '@gh/core-ui/monkey/monkey-drop-list-sorting-disabled.directive';

import { TaxRateFinderBusinessService, TaxRateFinderDialogComponent } from './accounting';
import {
  AuthNumberBusinessService,
  AuthNumberGenerationDialogComponent,
  AuthNumberRequestComponent,
  AuthNumberRequestDialogComponent,
} from './auth-number';
import {
  CommunicationsBusinessService,
  EmailDialogComponent,
  MeetingDialogComponent,
  SmsDialogComponent,
  SmsViewDialogComponent,
  WebsiteDialogComponent,
} from './communications';
import {
  GoogleDriveButtonComponent,
  SetFilterDialogComponent,
  HlpUpdateListingStatusDialogComponent,
  HlpQuoteCloneDataDialogComponent,
} from './components';
import {
  ContactFinderBusinessService,
  ContactFinderDialogComponent,
  ContactMenuComponent,
  ContactMenuControlDirective,
  ContactMenuDialogDirective,
} from './contacts';
import { CostCenterFinderBusinessService, CostCenterFinderDialogComponent } from './cost-centers';
import { AllowForSandboxDirective, AllowForViewDirective } from './directives';
import { EDocDetailsBusinessService, EDocDetailsDialogComponent } from './e-docs';
import {
  ComponentItemFinderBusinessService,
  ComponentItemFinderDialogComponent,
  FormulaCalculationService,
  FormulaPropagationService,
  PbComponentItemFinderBusinessService,
  PbComponentItemFinderCcViewComponent,
  PbComponentItemFinderDialogComponent,
  PbComponentItemFinderFullViewComponent,
  SalesTaxRateFinderBusinessService,
  SalesTaxRateFinderDialogComponent,
} from './estimating';
import {
  CommunityFinderBusinessService,
  CSMasterBusinessService,
  CSMasterFinderDialogComponent,
  DictionaryFinderBusinessService,
  EmailCampaignTemplateFinderBusinessService,
  GenericFinderDialogComponent,
  JobFinderBusinessService,
  JobFinderDialogComponent,
  JobSpecTemplateFinderBusinessService,
  JobSpecTemplateFinderDialogComponent,
  MasterZoneFinderBusinessService,
  OfficeFinderBusinessService,
  SalesPersonFinderBusinessService,
  ScheduleTemplateFinderDialogComponent,
  ScheduleTemplatesFinderBusinessService,
} from './finders';
import { FormulaBuilderComponent, FormulaContextLoader } from './formula';
import {
  HousePlanFinderBusinessService,
  HousePlanFinderDialogComponent,
  PbHousePlanFinderDialogComponent,
} from './house-plans';
import { PrinterBusinessService, PrinterFinderBusinessService, PrinterFinderDialogComponent } from './printers';
import { BuilderAuthGuard, RouterParentFocusedIdDirective } from './routing';
import {
  BuilderApplicationService,
  BuilderAuthNumberService,
  BuilderNavigationService,
  ReportService,
  WebSocketService,
  SmsService,
  MaintenanceService,
} from './services';
import { CommonActions, FormActionFlows, FormActionPipelines, FormActions, ListActionPipelines } from './skeletons';
import { BuilderApplicationStateFactory, BuilderFormSchemaFactory } from './state';
import { SwitchToBusinessService, SwitchToComponent } from './switch-to';
import { BuilderQuantityTypes, UomQuantityFormatPipe, UomQuantityPipe } from './types';
import { UsageDialogBusinessService, UsageDialogComponent } from './usage-dialog';
import { BuilderUserContext, BuilderUserContextService, BuilderUserProfileStore, ThemeService } from './user';
import { UserFinderBusinessService, UserFinderDialogComponent } from './users';
import {
  JobCsCreateDialogComponent,
  JobSpecCreateBusinessService,
  JobCsCreateBusinessService,
  JobSpecCreateDialogComponent,
  JobDetailToolbarComponent,
  JobDetailsReassignDialogComponent,
} from './jobs';
import {
  JobDetailsDrawsTabComponent,
  JobDrawDetailsDrawsFormState,
  JobDetailsDrawsPaymentScheduleSubtabComponent,
  JobDetailsDrawsLendersScheduleSubtabComponent,
  JobDetailsDrawsHistorySubtabComponent,
  JobDetailsDrawsFormComponent,
} from './jobs/components/job-details-draws-tab';
import { JobDetailsModel } from './jobs/services/jobt-details-model.service';
import { CoreUiExtSplitPaneModule } from '@gh/core-ui-ext/split-pane';
import { FilterRestService } from './filter';

@NgModule({
  imports: [
    DragDropModule,
    CommonInternalSharedModule.forChild(),
    CoreUiExtSplitPaneModule,
  ],
  declarations: [
    AllowForViewDirective,
    AllowForSandboxDirective,
    RouterParentFocusedIdDirective,

    AuthNumberRequestDialogComponent,
    AuthNumberRequestComponent,
    AuthNumberGenerationDialogComponent,
    ContactFinderDialogComponent,
    CostCenterFinderDialogComponent,
    FormulaBuilderComponent,
    SwitchToComponent,
    ComponentItemFinderDialogComponent,
    SalesTaxRateFinderDialogComponent,
    UsageDialogComponent,
    HousePlanFinderDialogComponent,
    UserFinderDialogComponent,
    PbHousePlanFinderDialogComponent,
    PbComponentItemFinderDialogComponent,
    SmsDialogComponent,
    EmailDialogComponent,
    MeetingDialogComponent,
    WebsiteDialogComponent,
    SmsViewDialogComponent,
    PbComponentItemFinderCcViewComponent,
    PbComponentItemFinderFullViewComponent,
    CSMasterFinderDialogComponent,
    JobSpecTemplateFinderDialogComponent,
    ScheduleTemplateFinderDialogComponent,
    JobFinderDialogComponent,
    PrinterFinderDialogComponent,
    ContactMenuComponent,
    ContactMenuControlDirective,
    ContactMenuDialogDirective,
    EDocDetailsDialogComponent,
    TaxRateFinderDialogComponent,
    GenericFinderDialogComponent,
    GoogleDriveButtonComponent,
    SetFilterDialogComponent,
    HlpQuoteCloneDataDialogComponent,
    HlpUpdateListingStatusDialogComponent,
    UomQuantityPipe,
    UomQuantityFormatPipe,

    // monkey patches
    MonkeyDropListSortingDisabledDirective,
    MonkeyDropListNoMoveDirective,
    JobCsCreateDialogComponent,
    JobSpecCreateDialogComponent,
    JobDetailsDrawsTabComponent,
    JobDetailToolbarComponent,
    JobDetailsReassignDialogComponent,
    JobDetailsDrawsFormComponent,
    JobDetailsDrawsHistorySubtabComponent,
    JobDetailsDrawsLendersScheduleSubtabComponent,
    JobDetailsDrawsPaymentScheduleSubtabComponent,
  ],
  exports: [
    CommonInternalSharedModule,

    RouterParentFocusedIdDirective,

    AllowForViewDirective,
    AllowForSandboxDirective,
    AuthNumberRequestComponent,
    FormulaBuilderComponent,
    SwitchToComponent,

    ContactMenuComponent,
    ContactMenuControlDirective,
    ContactMenuDialogDirective,
    GoogleDriveButtonComponent,
    SetFilterDialogComponent,
    HlpQuoteCloneDataDialogComponent,
    HlpUpdateListingStatusDialogComponent,
    UomQuantityPipe,
    UomQuantityFormatPipe,
  ],
  entryComponents: [
    AuthNumberRequestDialogComponent,
    AuthNumberGenerationDialogComponent,
    ContactFinderDialogComponent,
    CostCenterFinderDialogComponent,
    ComponentItemFinderDialogComponent,
    SalesTaxRateFinderDialogComponent,
    UsageDialogComponent,
    HousePlanFinderDialogComponent,
    UserFinderDialogComponent,
    PbHousePlanFinderDialogComponent,
    SmsDialogComponent,
    EmailDialogComponent,
    MeetingDialogComponent,
    SmsViewDialogComponent,
    WebsiteDialogComponent,
    PbComponentItemFinderDialogComponent,
    CSMasterFinderDialogComponent,
    JobSpecTemplateFinderDialogComponent,
    ScheduleTemplateFinderDialogComponent,
    JobFinderDialogComponent,
    PrinterFinderDialogComponent,
    EDocDetailsDialogComponent,
    TaxRateFinderDialogComponent,
    GenericFinderDialogComponent,
    SetFilterDialogComponent,
    HlpQuoteCloneDataDialogComponent,
    HlpUpdateListingStatusDialogComponent,
    JobCsCreateDialogComponent,
    JobSpecCreateDialogComponent,
    JobDetailsDrawsTabComponent,
    JobDetailToolbarComponent,
    JobDetailsReassignDialogComponent,
    JobDetailsDrawsFormComponent,
    JobDetailsDrawsHistorySubtabComponent,
    JobDetailsDrawsLendersScheduleSubtabComponent,
    JobDetailsDrawsPaymentScheduleSubtabComponent,
  ],
  schemas: [ NO_ERRORS_SCHEMA ],
})
export class BuilderSharedModule {
  static forRoot(): ModuleWithProviders<any> {
    return {
      ngModule: BuilderSharedModule,
      providers: [
        { provide: TYPE_SET, useValue: BuilderQuantityTypes, multi: true },
        { provide: BuilderUserContext, useClass: BuilderUserContextService },
        { provide: UserContext, useExisting: BuilderUserContext },
        { provide: AuthGuard, useClass: BuilderAuthGuard },
        { provide: ApplicationStateFactory, useExisting: BuilderApplicationStateFactory },
        { provide: FormSchemaFactory, useExisting: BuilderFormSchemaFactory },
        BuilderApplicationStateFactory,
        BuilderApplicationService,
        BuilderNavigationService,
        BuilderUserProfileStore,
        BuilderAuthNumberService,
        ReportService,
        AuthNumberBusinessService,
        CommonActions,
        FormActions,
        FormActionFlows,
        FormActionPipelines,
        ListActionPipelines,
        BuilderFormSchemaFactory,
        ContactFinderBusinessService,
        CostCenterFinderBusinessService,
        FormulaContextLoader,
        JobDrawDetailsDrawsFormState,
        SwitchToBusinessService,
        ComponentItemFinderBusinessService,
        SalesTaxRateFinderBusinessService,
        UsageDialogBusinessService,
        HousePlanFinderBusinessService,
        UserFinderBusinessService,
        PbComponentItemFinderBusinessService,
        CommunicationsBusinessService,
        JobSpecTemplateFinderBusinessService,
        ScheduleTemplatesFinderBusinessService,
        JobFinderBusinessService,
        CSMasterBusinessService,
        JobFinderBusinessService,
        PrinterFinderBusinessService,
        PrinterBusinessService,
        FormulaPropagationService,
        FormulaCalculationService,
        EDocDetailsBusinessService,
        TaxRateFinderBusinessService,
        EmailCampaignTemplateFinderBusinessService,
        MasterZoneFinderBusinessService,
        OfficeFinderBusinessService,
        CommunityFinderBusinessService,
        SalesPersonFinderBusinessService,
        DictionaryFinderBusinessService,
        ThemeService,
        WebSocketService,
        MaintenanceService,
        SmsService,
        JobCsCreateBusinessService,
        JobSpecCreateBusinessService,
        JobDetailsModel,
        FilterRestService,
      ],
    };
  }

  static forChild(): ModuleWithProviders<any> {
    return {
      ngModule: BuilderSharedModule,
      providers: [],
    };
  }
}
